import React, { useEffect, useState } from "react";
import { api } from "../api";
import NovelPaperbackCoverPilotB2cV1 from "./NovelPaperbackCoverPilotB2cV1";
import NovelExactPdfReviewPaneV1 from "./NovelExactPdfReviewPaneV1";
import NovelApprovedVersionsV1 from "./NovelApprovedVersionsV1";
import NovelRevisionDraftWorkspaceB2 from "./NovelRevisionDraftWorkspaceB2";
import NextScenesConfirmDialog from "./NextScenesConfirmDialog";

// An owner-only, opt-in Book Workspace step. The backend still authorizes
// every read/write. An ordinary private preview NEVER constitutes approval.
export const NOVEL_PRINT_ACKNOWLEDGMENT = "APPROVE AND LOCK THIS EXACT PDF";
const validId = (s) => /^[a-f0-9]{24}$/i.test(String(s || ""));
const validHash = (s) => /^[a-f0-9]{64}$/.test(String(s || ""));
export const matchingNovelPrintArtifact = (r, archive) => Boolean(
  r?.ok === true && validId(r.artifactId) && validHash(r.pdfSha256) &&
  Number.isInteger(r.physicalPageCount) && r.physicalPageCount >= 24 &&
  r.physicalPageCount % 2 === 0 &&
  ["prepared", "approved_locked"].includes(r.state) &&
  r.finalPageCountLocked === (r.state === "approved_locked") &&
  r.publicationEnabled === false && validId(archive?.snapshotId)
);

// Browser-side proof that the bytes seen or downloaded match the stored SHA.
export async function verifyNovelFinalPrintPdfV1(blob, expectedHash, headerHash) {
  if (!blob || blob.type !== "application/pdf" || blob.size < 100 ||
      blob.size > 8 * 1024 * 1024 || !validHash(expectedHash) ||
      headerHash !== expectedHash) throw new Error("Exact PDF identity mismatch.");
  const subtle = typeof window !== "undefined" ? window.crypto?.subtle : undefined;
  if (!subtle || typeof subtle.digest !== "function") {
    throw new Error("Secure PDF hash verification is unavailable in this browser.");
  }
  const digest = await subtle.digest("SHA-256", await blob.arrayBuffer());
  const actual = Array.from(new Uint8Array(digest), x => x.toString(16).padStart(2, "0")).join("");
  if (actual !== expectedHash) throw new Error("The received PDF bytes do not match the approved SHA-256.");
  return true;
}

export default function NovelFinalPrintApprovalV1({ storyId = "", isFR = false, onClose }) {
  const label = (en, fr) => isFR ? fr : en;
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [source, setSource] = useState(null);
  const [archive, setArchive] = useState(null);
  const [artifact, setArtifact] = useState(null);
  const [reviewUrl, setReviewUrl] = useState("");
  const [finalUrl, setFinalUrl] = useState("");
  const [reviewedHash, setReviewedHash] = useState("");
  const [ack, setAck] = useState(false);
  const [pendingPrintConfirmation, setPendingPrintConfirmation] = useState(null);
  const [activeRevisionId,setActiveRevisionId] = useState("");
  const [revisionBusy,setRevisionBusy] = useState(false);
  const [revisionError,setRevisionError] = useState("");
  const [revisionDirty,setRevisionDirty] = useState(false);
  const [pendingRevisionAction,setPendingRevisionAction] = useState(null);
  // Deliberately separate compile-time opt-in + exact pilot from final print.
  const revisionEnabled = process.env.REACT_APP_NOVEL_REVISION_DRAFT_V1_ENABLED === "true" &&
    process.env.REACT_APP_NOVEL_REVISION_DRAFT_V1_PILOT_STORY_ID === storyId &&
    validId(storyId);
  useEffect(() => () => {
    if (reviewUrl) URL.revokeObjectURL(reviewUrl);
    if (finalUrl) URL.revokeObjectURL(finalUrl);
  }, [reviewUrl, finalUrl]);

  useEffect(() => {
    let active = true;
    setSource(null); setArchive(null); setArtifact(null);
    setReviewUrl(""); setFinalUrl(""); setReviewedHash(""); setAck(false);
    setError(""); setBusy("loading");
    async function restore() {
      try {
        if (!validId(storyId)) throw new Error("A saved Novel ID is required.");
        const saved = await api.getNovelSavedInteriorStatus(storyId);
        const latest = saved?.latestSnapshot;
        if (saved?.ok !== true || saved.sourceReady !== true ||
            !validHash(saved.fingerprint) || !validId(latest?.snapshotId) ||
            !validHash(latest?.fingerprint)) {
          throw new Error("Verify an existing saved production archive before final print approval. No new snapshot will be captured here.");
        }
        if (saved.fingerprint !== latest.fingerprint) {
          throw new Error("The saved manuscript differs from the latest archive. Final approval is blocked.");
        }
        const verified = await api.getNovelProductionSnapshotForFinalPrint(storyId, latest.snapshotId);
        if (verified?.ok !== true || verified.status !== "source-captured-only" ||
            verified.fingerprint !== latest.fingerprint || verified.snapshotId !== latest.snapshotId ||
            verified.manuscriptLocked !== false || verified.printPdfReady !== false) {
          throw new Error("The saved archive could not be verified.");
        }
        let current;
        try {
          current = await api.getNovelPaperbackInteriorCurrent(storyId, latest.snapshotId);
        } catch (e) {
          if (e?.status === 404 && e?.data?.code === "novel_print_artifact_not_prepared") current = null;
          else throw e;
        }
        if (current && !matchingNovelPrintArtifact(current, latest)) {
          throw new Error("The stored final-print artifact is inconsistent.");
        }
        if (!active) return;
        setSource(saved); setArchive(latest); setArtifact(current);
      } catch (e) {
        if (active) setError(e?.status === 404
          ? (isFR ? "L'approbation finale n'est pas activée sur ce serveur."
            : "Final print approval is not available on this server.")
          : String(e?.message || "Final print authority is unavailable."));
      } finally { if (active) setBusy(""); }
    }
    restore();
    return () => { active = false; };
  }, [storyId, isFR]);

  const eligible = Boolean(source?.fingerprint && archive?.snapshotId &&
    source.fingerprint === archive.fingerprint && !error && !busy);
  const validArtifact = artifact && matchingNovelPrintArtifact(artifact, archive);

  async function prepare() {
    if (!eligible || artifact) return;
    setBusy("prepare"); setError("");
    try {
      const created = await api.prepareNovelPaperbackInterior(storyId, archive.snapshotId, archive.fingerprint);
      const current = await api.getNovelPaperbackInteriorCurrent(storyId, archive.snapshotId);
      if (!created?.ok || !matchingNovelPrintArtifact(current, archive) ||
          current.artifactId !== created.artifactId || current.pdfSha256 !== created.pdfSha256 ||
          current.physicalPageCount !== created.pageCount || current.state !== "prepared") {
        throw new Error("The prepared exact PDF did not match its durable record.");
      }
      setArtifact(current);
    } catch (e) { setError(String(e?.message || "Unable to prepare the exact PDF.")); }
    finally { setBusy(""); }
  }

  async function review() {
    if (!eligible || !validArtifact || artifact.state !== "prepared") return;
    setBusy("review"); setError(""); setReviewedHash(""); setAck(false); setReviewUrl("");
    try {
      const pdf = await api.getNovelPaperbackInteriorPdf(storyId, archive.snapshotId, artifact.artifactId, "review");
      await verifyNovelFinalPrintPdfV1(pdf.blob, artifact.pdfSha256, pdf.sha256);
      setReviewUrl(URL.createObjectURL(pdf.blob));
      setReviewedHash(artifact.pdfSha256);
    } catch (e) { setError(String(e?.message || "Unable to inspect the exact PDF.")); }
    finally { setBusy(""); }
  }

  function requestPrintApproval() {
    if (!eligible || !validArtifact || artifact.state !== "prepared" ||
        !ack || reviewedHash !== artifact.pdfSha256 || !reviewUrl) return;
    setPendingPrintConfirmation({ storyId, snapshotId: archive.snapshotId,
      artifactId: artifact.artifactId, pdfSha256: artifact.pdfSha256,
      physicalPageCount: artifact.physicalPageCount });
  }
  async function approve() {
    const confirmed = pendingPrintConfirmation;
    setPendingPrintConfirmation(null);
    if (!confirmed || !eligible || !validArtifact || artifact.state !== "prepared" ||
        !ack || reviewedHash !== artifact.pdfSha256 || !reviewUrl ||
        confirmed.storyId !== storyId || confirmed.snapshotId !== archive.snapshotId ||
        confirmed.artifactId !== artifact.artifactId ||
        confirmed.pdfSha256 !== artifact.pdfSha256 ||
        confirmed.physicalPageCount !== artifact.physicalPageCount) return;
    setBusy("approve"); setError("");
    try {
      const result = await api.approveNovelPaperbackInterior(storyId, archive.snapshotId,
        artifact.artifactId, artifact.pdfSha256, NOVEL_PRINT_ACKNOWLEDGMENT);
      const current = await api.getNovelPaperbackInteriorCurrent(storyId, archive.snapshotId);
      if (result?.ok !== true || !matchingNovelPrintArtifact(current, archive) ||
          current.artifactId !== artifact.artifactId || current.pdfSha256 !== artifact.pdfSha256 ||
          current.physicalPageCount !== artifact.physicalPageCount ||
          result.finalPageCount !== artifact.physicalPageCount || current.state !== "approved_locked") {
        throw new Error("Approval outcome requires verification; reload before any other action.");
      }
      setArtifact(current); setReviewUrl(""); setReviewedHash(""); setAck(false);
    } catch (e) { setError(String(e?.message || "Approval was not confirmed. Reload before retrying.")); }
    finally { setBusy(""); }
  }

  async function download() {
    if (!eligible || !validArtifact || artifact.state !== "approved_locked") return;
    setBusy("download"); setError(""); setFinalUrl("");
    try {
      const pdf = await api.getNovelPaperbackInteriorPdf(storyId, archive.snapshotId, artifact.artifactId, "final");
      if (Number(pdf.lockedPageCount) !== artifact.physicalPageCount) {
        throw new Error("Final page-count lock mismatch.");
      }
      await verifyNovelFinalPrintPdfV1(pdf.blob, artifact.pdfSha256, pdf.sha256);
      setFinalUrl(URL.createObjectURL(pdf.blob));
    } catch (e) { setError(String(e?.message || "Final PDF download is unavailable.")); }
    finally { setBusy(""); }
  }

  function startPrivateRevision(v) {
    if (!revisionEnabled || !v?.coverApproved || revisionBusy || activeRevisionId ||
        revisionDirty || !validId(v?.snapshotId) ||
        !validId(v?.interiorArtifactId) || !validId(v?.coverArtifactId)) return;
    // Reuse the existing NextScenes confirmation dialog. No API call until confirmed.
    setPendingRevisionAction({kind:"start",version:v});
  }
  async function confirmRevisionAction(){
    const pending=pendingRevisionAction;
    setPendingRevisionAction(null);
    if(!pending)return;
    if(pending.kind==="close"){onClose?.();return;}
    const v=pending.version;
    if(pending.kind!=="start" || !revisionEnabled || revisionBusy ||
       activeRevisionId || revisionDirty || !v?.coverApproved ||
       !validId(v.snapshotId)||!validId(v.interiorArtifactId)||
       !validId(v.coverArtifactId))return;
    setRevisionBusy(true);setRevisionError("");
    try{
      const result=await api.post(
        `/api/stories/${encodeURIComponent(storyId)}/production-revisions-v1`,
        {snapshotId:v.snapshotId,interiorArtifactId:v.interiorArtifactId,
          coverArtifactId:v.coverArtifactId});
      if(result?.ok!==true || !validId(result.revisionId) ||
         result.storyId!==storyId || result.sourceSnapshotId!==v.snapshotId ||
         result.sourceInteriorArtifactId!==v.interiorArtifactId ||
         result.sourceCoverArtifactId!==v.coverArtifactId ||
         result.sourceInteriorPdfSha256!==v.interiorPdfSha256 ||
         result.sourceCoverPdfSha256!==v.coverPdfSha256)
        throw new Error("Private revision identity could not be confirmed.");
      setActiveRevisionId(result.revisionId);setRevisionDirty(false);
    }catch(e){setRevisionError(String(e?.message||"Could not open the private revision."));}
    finally{setRevisionBusy(false);}
  }
  function closeRevision() {
    setActiveRevisionId("");setRevisionDirty(false);setRevisionError("");
  }
  function closePanel(){
    if(revisionDirty){setPendingRevisionAction({kind:"close"});return;}
    onClose?.();
  }

  return (
    <section className="ns-card" data-novel-final-print-approval-v1="owner-only"
      style={{ padding: 22, borderRadius: 18, display: "grid", gap: 18,
        border: "1px solid #cbd5d1", borderLeft: "5px solid #0f766e", background: "#fbfcfd" }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{fontSize:12,fontWeight:800,letterSpacing:"0.1em",
            textTransform:"uppercase",color:"#64748b",marginBottom:5}}>
            {activeRevisionId ? label("Book Versions · Private Revision","Versions du livre · Révision privée") : label("Book Versions · Publication","Versions du livre · Publication")}
          </div>
          <h3 style={{ margin: "0 0 6px", fontSize:25, lineHeight:1.25, color:"#123b32" }}>
            {activeRevisionId ? label("Private Revision", "Révision privée") : label("Final Print Approval", "Approbation finale de l'intérieur")}</h3>
          {!activeRevisionId ? <p className="ns-card-meta" style={{ margin: 0 }}>
            {label("Review approved book files and their history. Existing approvals remain locked; viewing does not publish the book.",
              "Consultez les fichiers approuvés du livre et leur historique. Les approbations restent verrouillées ; la consultation ne publie pas le livre.")}
          </p> : null}</div>
        <button type="button" className="ns-button ns-button-outline" onClick={closePanel}>
          {label("Close", "Fermer")}</button>
      </div>
      {!activeRevisionId ? <NovelApprovedVersionsV1 storyId={storyId} isFR={isFR}
        revisionEnabled={revisionEnabled} onStartRevision={startPrivateRevision} /> : null}
      {!activeRevisionId ? <section data-novel-current-print-approval="existing-flow" style={{padding:"17px 20px",
        borderRadius:14,border:"1px solid #9fcdb6",background:"#f4fbf6",
        display:"grid",gap:12}}>
        <div>
          <div style={{fontSize:12,fontWeight:800,letterSpacing:"0.09em",
            textTransform:"uppercase",color:"#047857"}}>
            {label("Current approval · Existing workflow","Approbation actuelle · Parcours existant")}
          </div>
          <h4 style={{margin:"4px 0 0",fontSize:18,color:"#14532d"}}>
            {label("Approved File Status and PDF Review","Statut du fichier approuvé et vérification PDF")}
          </h4>
        </div>
      {busy === "loading" ? <p role="status">{label("Verifying saved authority…", "Vérification de l'archive…")}</p> : null}
      {error ? <p role="alert" style={{ color: "#9a3412" }}>{error}</p> : null}
      {archive ? <details data-novel-technical-archive="read-only"
        style={{fontSize:13,overflowWrap:"anywhere"}}>
        <summary style={{cursor:"pointer",fontWeight:650,color:"#334155"}}>
          {label("Verified archive details","Détails de l'archive vérifiée")}
        </summary>
        <p className="ns-card-meta">
          {label("Verified archive", "Archive vérifiée")}: <code>{archive.snapshotId}</code>
          {" · "}{label("Source fingerprint", "Empreinte source")}: <code>{archive.fingerprint}</code>
        </p>
      </details> : null}
      {validArtifact ? <div data-novel-final-print-artifact-state={artifact.state}
        style={{padding:"12px 14px",background:"#fff",borderRadius:10,
          border:"1px solid #bbdbc8"}}>
        <strong>{artifact.state === "approved_locked" ? label("Approved and locked", "Approuvé et verrouillé") :
          label("Prepared for creator review", "Préparé pour la vérification de l'auteur")}</strong>
        <p className="ns-card-meta">{label("Physical page count", "Nombre de pages physiques")}: <strong>{artifact.physicalPageCount}</strong>
          {" · "}SHA-256: <code style={{ overflowWrap: "anywhere" }}>{artifact.pdfSha256}</code></p>
      </div> : null}
      {eligible && !artifact ? (
        <button type="button" className="ns-button ns-button-outline" disabled={Boolean(busy)}
          onClick={prepare} data-novel-final-print-prepare="true">
          {label("Prepare Exact Review PDF", "Préparer le PDF exact à vérifier")}</button>
      ) : null}
      {eligible && validArtifact && artifact.state === "prepared" ? <>
        <button type="button" className="ns-button ns-button-outline" disabled={Boolean(busy)}
          data-novel-final-print-review="true" onClick={review}>
          {label("Open Exact Review PDF", "Ouvrir le PDF exact")}</button>
        {reviewUrl ? <>
          <NovelExactPdfReviewPaneV1 pdfUrl={reviewUrl} kind="interior" isFR={isFR} />
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
            <span>{label("I have inspected this exact PDF and authorize locking its immutable bytes and final page count.",
              "J'ai vérifié ce PDF exact et j'autorise le verrouillage de ses octets et de sa pagination.")}</span>
          </label>
          <button type="button" className="ns-button ns-button-primary"
            data-novel-final-print-approve="true" onClick={requestPrintApproval}
            disabled={!ack || Boolean(busy) || reviewedHash !== artifact.pdfSha256}>
            {label("Approve and Lock This Exact PDF", "Approuver et verrouiller ce PDF exact")}</button>
        </> : null}
      </> : null}
      {eligible && validArtifact && artifact.state === "approved_locked" &&
        process.env.REACT_APP_NOVEL_PAPERBACK_COVER_B2C_V1_ENABLED !== "true" ? <>
        <button type="button" className="ns-button ns-button-primary"
          data-novel-final-print-download="true" disabled={Boolean(busy)} onClick={download}>
          {label("Verify and Load Approved PDF", "Vérifier et charger le PDF approuvé")}</button>
        {finalUrl ? <a className="ns-button ns-button-outline" href={finalUrl}
          download="NextScenes_Approved_Novel_Interior.pdf" data-novel-final-print-save="true">
          {label("Save Approved Interior PDF", "Enregistrer le PDF intérieur approuvé")}</a> : null}
      </> : null}
      {eligible && validArtifact && artifact.state === "approved_locked" &&
        process.env.REACT_APP_NOVEL_PAPERBACK_COVER_B2C_V1_ENABLED === "true" ? (
        <NovelPaperbackCoverPilotB2cV1 storyId={storyId} snapshotId={archive.snapshotId}
          interiorArtifactId={artifact.artifactId} sourceFingerprint={archive.fingerprint}
          interiorPdfSha256={artifact.pdfSha256} physicalPageCount={artifact.physicalPageCount}
          isFR={isFR} />
      ) : null}
      </section> : null}
      <section data-novel-revision-stage="preview-only" style={{padding:"20px 22px",
        borderRadius:14,border:"1px solid #bfcee4",borderLeft:"5px solid #4f74aa",
        background:"#f4f7fc",display:"grid",gap:8}}>
        {!activeRevisionId ? <div style={{fontSize:12,fontWeight:800,letterSpacing:"0.1em",
          textTransform:"uppercase",color:"#49668f"}}>
          {label("Step 2 · Revision Preparation","Étape 2 · Préparation de la révision")}
        </div> : null}
        <h4 style={{margin:0,fontSize:19,lineHeight:1.3,color:"#223d69"}}>
          {label("Working Revision","Révision en cours")}
        </h4>
        {!activeRevisionId ? <p style={{margin:0,fontSize:14,lineHeight:1.6,color:"#41516a"}}>
          {label("A private revision will start from a selected matching approved pair. Your current Canon and approved PDFs will remain unchanged.",
            "Une révision privée partira d'une paire de fichiers approuvés. Le Canon actuel et les PDF approuvés resteront inchangés.")}
        </p> : null}
        {revisionEnabled ? <>
          {!activeRevisionId ? <p style={{margin:0,fontSize:13,color:"#41516a"}}>
            {label("Select Start / Continue Revision beside a matching approved pair in the archive above.",
              "Choisissez Commencer / Reprendre la révision pour une paire approuvée ci-dessus.")}
          </p> : null}
          {revisionBusy ? <p role="status">{label("Opening private revision…","Ouverture de la révision…")}</p> : null}
          {revisionError ? <p role="alert" style={{color:"#991b1b"}}>{revisionError}</p> : null}
          {activeRevisionId ? <NovelRevisionDraftWorkspaceB2
            storyId={storyId} revisionId={activeRevisionId} isFR={isFR}
            onBack={closeRevision} onDirtyChange={setRevisionDirty} /> : null}
        </> : <>
          <div><button type="button" className="ns-button ns-button-outline"
            data-novel-revision-start="not-enabled" disabled>
            {label("Start Revision · Not Yet Enabled","Commencer une révision · Pas encore disponible")}
          </button></div>
          <small style={{color:"#64748b"}}>
            {label("Private revision editing is not enabled for this book. Your approved files and current manuscript remain unchanged.",
              "La révision privée n'est pas activée pour ce livre. Vos fichiers approuvés et votre manuscrit actuel restent inchangés.")}
          </small>
        </>}
      </section>
      {!activeRevisionId ? <section data-novel-revision-next-approval="informational-only"
        style={{padding:"14px 18px",border:"1px dashed #cbd5e1",
          borderRadius:12,background:"#f8fafc"}}>
        <div style={{fontSize:12,fontWeight:800,color:"#64748b",
          textTransform:"uppercase",letterSpacing:"0.09em"}}>
          {label("Step 3 · Later","Étape 3 · À venir")}
        </div>
        <h4 style={{fontSize:16,margin:"5px 0",color:"#475569"}}>
          {label("Review and Approve a Revised Version","Vérifier et approuver une version révisée")}
        </h4>
        <p style={{fontSize:13,margin:0,lineHeight:1.6,color:"#64748b"}}>
          {label("This is a future step. It does not unlock or replace any approved file.",
            "Cette étape viendra plus tard. Elle ne déverrouille ni ne remplace aucun fichier approuvé.")}
        </p>
      </section> : null}
      {!activeRevisionId ? <p className="ns-card-meta" style={{ margin: 0 }}>
        {label(process.env.REACT_APP_NOVEL_PAPERBACK_COVER_B2C_V1_ENABLED === "true"
          ? "No manuscript recapture, KDP submission or publishing occurs here."
          : "No manuscript recapture, cover approval, KDP submission or publishing occurs here.",
          process.env.REACT_APP_NOVEL_PAPERBACK_COVER_B2C_V1_ENABLED === "true"
          ? "Aucune nouvelle capture du manuscrit, soumission KDP ou publication n'a lieu ici."
          : "Aucune nouvelle capture du manuscrit, approbation de couverture, soumission KDP ou publication n'a lieu ici.")}
      </p> : null}
      <NextScenesConfirmDialog
        open={Boolean(pendingPrintConfirmation)}
        title={label("Lock this exact interior PDF?", "Verrouiller ce PDF intérieur exact ?")}
        message={label(
          "This permanently locks the exact reviewed PDF bytes and final page count. This decision cannot be undone. Cancel to keep the current review unchanged.",
          "Cette action verrouille définitivement ce PDF exact et sa pagination. Elle est irréversible. Annulez pour conserver l'examen actuel.")}
        stayLabel={label("Cancel", "Annuler")}
        leaveLabel={label("Approve and Lock Interior", "Approuver et verrouiller l'intérieur")}
        onStay={() => setPendingPrintConfirmation(null)}
        onLeave={() => { void approve(); }}
      />
      <NextScenesConfirmDialog
        open={Boolean(pendingRevisionAction)}
        title={pendingRevisionAction?.kind==="close"
          ? label("Leave private revision?","Quitter la révision privée ?")
          : label("Start or resume private revision?","Commencer ou reprendre une révision privée ?")}
        message={pendingRevisionAction?.kind==="close"
          ? label("Unsaved changes in this revision will be lost. Keep editing or leave without saving.",
              "Les modifications non enregistrées seront perdues. Continuer ou quitter sans enregistrer.")
          : label("Create or resume a private revision from this exact approved interior and cover. The approved originals remain locked.",
              "Créer ou reprendre une révision privée depuis ces fichiers approuvés. Les originaux restent verrouillés.")}
        stayLabel={label("Cancel","Annuler")}
        leaveLabel={pendingRevisionAction?.kind==="close"
          ? label("Leave Without Saving","Quitter sans enregistrer")
          : label("Start / Continue Revision","Commencer / Reprendre la révision")}
        onStay={()=>setPendingRevisionAction(null)}
        onLeave={confirmRevisionAction}
      />
    </section>
  );
}
